import { useNavigate, Routes, Route, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useCallback, useState } from "react";
import userService from "services/user.service";
import { logout, setPermissions, setCustomization } from "./actions/auth";
import { clearMessage } from "./actions/message";
import Toolbar from "components/Toolbar";
import Login from "./pages/Login";
import Home from "pages/Home";
import Calls from "pages/Calls";
import DataUpdate from "pages/DataUpdate";
import WorkingHours from "pages/WorkingHours";
import Tracking from "pages/Tracking";
import TrackingForOddo from 'pages/TrackingForOddo'
import ResetPassword from "pages/ResetPassword";
import Profile from "pages/Profile";
import Dashboard from "pages/Dashboard";
import Trucks from "pages/Trucks";
import CreateTruck from "pages/TruckCreate";
import EditTruck from "pages/TruckEdit";
import RfqNotifications from "pages/RfqNotifications";
import { EmployeesInfo } from "pages/EmployeesInfo/EmployeesInfo";
import NotFound from "components/NotFound";
import { prepareTenantFromUrl } from 'utils/tenantHelper'
import { url } from "utils/constants";
import { getTokenFromUrl } from 'utils/tokenHelper';
import GmailAccessPopup from "components/GmailAccessPopup/GmailAccessPopup";

import "./App.scss";
import classNames from "classnames";
import Provider from "pages/Provider";
import DashboardList from "pages/DashboardList";

function App() {
  const location = useLocation();
  const dispatch = useDispatch();
  const { isLoggedIn } = useSelector((state) => state.auth);
  const permissions = useSelector((state) => state.permissions);
  const localPermissions = localStorage.getItem("permissions") && JSON.parse(localStorage.getItem("permissions"))
  const localCustomisation = localStorage.getItem("customization") && JSON.parse(localStorage.getItem("customization"))
  const navigate = useNavigate();
  const bypassToken = getTokenFromUrl(); // if truthy, we skip auth
  const [showGmailPopup, setShowGmailPopup] = useState(false);
  const [gmailAuthUrl, setGmailAuthUrl] = useState("");

  useEffect(() => {
    if (!prepareTenantFromUrl(url)) {
      navigate("/tenant");
    } else if (
      !isLoggedIn &&
      !bypassToken &&
      !location.pathname.includes("/api/v1/password/reset/confirm/") &&
      !location.pathname.includes("/api/v1/user/profile/account_create_confirm/")
    ) {
      navigate("/login");
    }
  }, [navigate, isLoggedIn, location.pathname, bypassToken]);

  useEffect(() => {
    if (["/login"].includes(location.pathname)) {
      dispatch(clearMessage());
    }
  }, [dispatch, location]);

  const logOut = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  const permissionsCall = () => {
    if(localPermissions) {
      dispatch(setPermissions(localPermissions));
    } else {
      userService.getUserRole().then(
        (response) => {
          dispatch(setPermissions(response.data?.permissions));
          localStorage.setItem("permissions", JSON.stringify(response.data?.permissions));
        },
        () => {
          logOut();
        },
      );
    }
  }

  const customizationCall = () => {
    if(localCustomisation) {
      dispatch(setCustomization(localCustomisation));
      localStorage.setItem("customization", JSON.stringify(localCustomisation));
      let link = document.querySelector("link[rel~='icon']");
        if (!link) {
          link = document.createElement("link");
          link.rel = "icon";
          document.getElementsByTagName("head")[0].appendChild(link);
        }
        link.href = localCustomisation.favicon_url;
        document.title = `${localCustomisation.customer_name} Platform internal`;
        document.documentElement.style.setProperty(
          "--hover_color",
          "#" + localCustomisation.hover_color,
        );
        document.documentElement.style.setProperty(
          "--main_color",
          "#" + localCustomisation.main_color,
        );
        document.documentElement.style.setProperty(
          "--text_color",
          "#" + localCustomisation.text_color,
        );
    } else {
      userService.getCustomisation().then((response) => {
        dispatch(setCustomization(response.data));
        localStorage.setItem("customization", JSON.stringify(response.data));
        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
          link = document.createElement("link");
          link.rel = "icon";
          document.getElementsByTagName("head")[0].appendChild(link);
        }
        link.href = response.data.favicon_url;
        document.title = `${response.data.customer_name} Platform internal`;
        document.documentElement.style.setProperty(
          "--hover_color",
          "#" + response.data.hover_color,
        );
        document.documentElement.style.setProperty(
          "--main_color",
          "#" + response.data.main_color,
        );
        document.documentElement.style.setProperty(
          "--text_color",
          "#" + response.data.text_color,
        );
      })
    }
  }

  const checkGmailAccessCall = () => {
    userService.checkGmailAccess().then(
      (response) => {
        if (response.data.has_access === false && response.data.authorization_url) {
          setGmailAuthUrl(response.data.authorization_url);
          setShowGmailPopup(true);
        }
      },
      (error) => {
        console.error("Gmail Access Error:", error);
      }
    );
  }

  useEffect(() => {
    if (isLoggedIn && !bypassToken) {
      permissionsCall();
      customizationCall();
      checkGmailAccessCall();
    } else {
      localStorage.removeItem("customization");
      localStorage.removeItem("permissions");
    }
  }, [isLoggedIn, bypassToken]);

  return (
    <>
      {isLoggedIn && <Toolbar logOut={logOut} permissions={permissions} />}
      <div
        className={classNames(
          !location.pathname.includes("request_for_quote_page") ? "main" : "",
          isLoggedIn && "main with-toolbar",
        )}
      >
        <Routes>
          <Route path="/" element={<Home />} />
          {permissions?.calls && (
            <Route path="/calls" element={<Calls isLoggedIn={isLoggedIn} />} />
          )}
          {permissions?.calls_and_tracking && (
            <Route path="/tracking" element={<Tracking isLoggedIn={isLoggedIn} />} />
          )}
          {permissions?.working_hours && (
            <Route
              path="/working-hours"
              element={<WorkingHours isLoggedIn={isLoggedIn} />}
            />
          )}
          {permissions?.data_update && (
            <Route path="/data-update" element={<DataUpdate isLoggedIn={isLoggedIn} />} />
          )}
          {permissions?.profile && (
            <Route path="/profile" element={<Profile isLoggedIn={isLoggedIn} />} />
          )}
          <Route path="/login" element={<Login />} />
          <Route path="/tenant" element={<Provider />} />
          <Route
            path="/api/v1/password/reset/confirm/:uid/:token"
            element={<ResetPassword title="Change Password" />}
          />
          <Route
            path="/api/v1/user/profile/account_create_confirm/:uid/"
            element={<ResetPassword title="Set Password" />}
          />

          {permissions?.completeness_info && (
            <Route path="/completeness_info" element={<EmployeesInfo />} />
          )}
          {(permissions?.rfq_all_trucks || permissions?.rfq_dispatched_trucks) && (
            <Route path="/request_for_quote_page" element={<Dashboard />} />
          )}
          {permissions?.rfq_list  && (
            <Route path="/request_for_quote_page_list" element={<DashboardList />} />
          )}

          {permissions?.manage_dispatched_trucks && (
            <>
              <Route path="/trucks" element={<Trucks />} />
              <Route path="/create_truck" element={<CreateTruck />} />
              <Route path="/edit_truck" element={<EditTruck />} />
            </>
          )}

          {(permissions?.rfq_notification?.manage_rfq_notifications || permissions?.rfq_notification?.manage_rfq_contact_blacklist) && (
            <Route path="/rfq-notifications/*" element={<RfqNotifications />} />
          )}

          <Route
            path="/calls-traking-report-odoo"
            element={<TrackingForOddo isLoggedIn={isLoggedIn} bypassToken={bypassToken}/>}
          />

          {/* // Not found page */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
      {showGmailPopup && (
        <GmailAccessPopup
          setShowModal={setShowGmailPopup}
          authorizationUrl={gmailAuthUrl}
        />
      )}
    </>
  );
}

export default App;
